import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { isTauriApp } from "@/lib/tauri-auth"
import { useEffect } from "react"
import { AlertCircle } from "lucide-react"

interface NdiController {
  isAvailable: boolean
  isActive: boolean
  error: string | null
  toggleSender: (enabled: boolean, config?: { width: number; height: number }) => Promise<void>
  checkAvailability: () => Promise<void>
}

interface NdiControlProps {
  ndi: NdiController
  width: number
  height: number
}

export function NdiControl({ ndi, width, height }: NdiControlProps) {
  const isTauri = isTauriApp()

  // Vérifier la disponibilité de NDI au montage uniquement
  // IMPORTANT: ne pas dépendre de `ndi` (objet recréé à chaque render) sinon
  // checkAvailability est spammé en boucle et engorge le canal IPC.
  const checkAvailability = ndi.checkAvailability
  useEffect(() => {
    if (isTauri) {
      checkAvailability()
    }
  }, [isTauri, checkAvailability])

  const handleToggle = async (enabled: boolean) => {
    if (!isTauri || !ndi.isAvailable) {
      return
    }

    try {
      // Utiliser la résolution actuelle du canvas pour s'adapter dynamiquement
      await ndi.toggleSender(enabled, { width, height })
    } catch (err) {
      console.error('Erreur lors du toggle NDI:', err)
    }
  }

  // Ne pas afficher si pas sur Tauri
  if (!isTauri) {
    return null
  }

  return (
    <div className="flex items-center space-x-2">
      <Switch
        id="ndi-toggle"
        checked={ndi.isActive}
        disabled={!ndi.isAvailable}
        onCheckedChange={handleToggle}
      />
      <Label htmlFor="ndi-toggle" className={ndi.isAvailable ? "cursor-pointer" : "cursor-not-allowed opacity-60"}>
        NDI
      </Label>
      {!ndi.isAvailable && (
        <div className="flex items-center space-x-1 text-xs text-red-600 dark:text-red-400" title={ndi.error ?? undefined}>
          <AlertCircle className="h-3 w-3" />
          <span>Runtime absent</span>
        </div>
      )}
      {ndi.isAvailable && ndi.error && (
        <div className="flex items-center space-x-1 text-xs text-red-600 dark:text-red-400" title={ndi.error ?? undefined}>
          <AlertCircle className="h-3 w-3" />
          <span>Erreur</span>
        </div>
      )}
    </div>
  )
}
